import * as React from 'react';
import { createTheme } from '@mui/material/styles';
import '@fontsource/pretendard';

// 라이트/다크 테마 전환용 컨텍스트
export const ColorModeContext = React.createContext<{ toggleMode: () => void }>({
    toggleMode: () => {},
});

// 로고에서 뽑아낸 브랜드 블루. 강조색은 이 네 가지 밖으로 나가지 않는다.
export const brandColors = {
    deep: '#2B65AE',
    blue: '#49A1FA',
    sky: '#84CCFC',
    mist: '#C8E8FD',
} as const;

// 경로·수치·단위처럼 '기계가 읽는 값'에만 쓰는 고정폭 스택.
export const monoFontFamily =
    "'SFMono-Regular', ui-monospace, Menlo, Consolas, 'Liberation Mono', monospace";

// MUI Typography에 'mono' variant를 추가한다 (고정폭 값/메타 표기 전용).
// CRA(isolatedModules)에서는 이 선언이 모듈 파일(이 파일처럼 import/export가 있는 파일) 안에 있어야 동작한다.
declare module '@mui/material/styles' {
    interface TypographyVariants {
        mono: React.CSSProperties;
    }
    interface TypographyVariantsOptions {
        mono?: React.CSSProperties;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        mono: true;
    }
}

type ColorMode = 'light' | 'dark';

function makeTheme(mode: ColorMode) {
    const tokens = {
        light: {
            background: { default: '#F1F4F8', paper: '#FFFFFF' },
            text: { primary: '#121821', secondary: '#586579', disabled: '#8C97A8' },
            divider: '#DBE1EA',
            primary: { main: brandColors.deep, contrastText: '#FFFFFF' },
            selection: 'rgba(73, 161, 250, 0.28)',
        },
        dark: {
            background: { default: '#0F141C', paper: '#161D28' },
            text: { primary: '#E6EBF3', secondary: '#8D99AC', disabled: '#6B7789' },
            divider: '#262F3D',
            primary: { main: brandColors.blue, contrastText: '#0B1018' },
            selection: 'rgba(73, 161, 250, 0.32)',
        },
    }[mode];

    return createTheme({
        palette: {
            mode,
            background: tokens.background,
            text: tokens.text,
            divider: tokens.divider,
            primary: tokens.primary,
        },
        shape: {
            // 14px짜리 둥근 모서리는 어디서 본 듯한 인상을 준다. 각을 살짝 세워 도구다운 인상으로.
            borderRadius: 8,
        },
        typography: {
            fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', Arial, sans-serif",
            // h2~h6은 페이지에서 결과값 표시용(큰 숫자)으로 쓰고 있어 크기는 MUI 기본값을 유지하고,
            // 굵기와 자간만 브랜드 톤에 맞춘다.
            h1: {
                fontWeight: 800,
                fontSize: '28px',
                letterSpacing: '-0.03em',
                lineHeight: 1.25,
                '@media (max-width:600px)': {
                    fontSize: '24px',
                },
            },
            h2: { fontWeight: 700, letterSpacing: '-0.03em' },
            h3: { fontWeight: 700, letterSpacing: '-0.03em' },
            h4: { fontWeight: 700, letterSpacing: '-0.025em' },
            h5: { fontWeight: 700, letterSpacing: '-0.02em' },
            h6: { fontWeight: 700, fontSize: '17px', letterSpacing: '-0.02em' },
            // 섹션/카드 제목용. h6(17px)과 겹치지 않게 한 단계 낮춰서 위계를 만든다.
            subtitle1: { fontSize: '16px', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.4 },
            // 작은 제목/항목 라벨용.
            subtitle2: { fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.5 },
            body1: {
                fontSize: '15px',
                lineHeight: 1.7,
                '@media (max-width:600px)': {
                    fontSize: '14px',
                },
            },
            body2: {
                fontSize: '13px',
                lineHeight: 1.65,
                '@media (max-width:600px)': {
                    fontSize: '12px',
                },
            },
            // 보조 설명용.
            caption: { fontSize: '12px', lineHeight: 1.5 },
            // 섹션 라벨용. 고정폭 + 넓은 자간으로 '설계 도면 라벨' 같은 인상을 준다.
            overline: {
                fontFamily: monoFontFamily,
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.16em',
                lineHeight: 1.6,
                textTransform: 'uppercase',
            },
            // 경로·수치·해시처럼 고정폭으로 보여줄 값 전용.
            mono: { fontFamily: monoFontFamily, fontSize: '12px', lineHeight: 1.6 },
            button: {
                letterSpacing: '-0.01em',
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: tokens.background.default,
                        WebkitFontSmoothing: 'antialiased',
                    },
                    '::selection': {
                        backgroundColor: tokens.selection,
                    },
                    // 키보드 포커스는 브랜드 색 링으로 통일한다.
                    'a:focus-visible, button:focus-visible, [tabindex]:focus-visible': {
                        outline: `2px solid ${tokens.primary.main}`,
                        outlineOffset: '2px',
                        borderRadius: '4px',
                    },
                },
            },
            MuiContainer: {
                defaultProps: {
                    maxWidth: 'md',
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        backgroundImage: 'none',
                        border: `1px solid ${tokens.divider}`,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        borderRadius: '6px',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        backgroundColor: tokens.background.paper,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: '4px',
                        fontWeight: 600,
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: tokens.divider,
                    },
                    // size="small" 토글 버튼의 글자 크기·패딩을 이 값으로 고정한다.
                    sizeSmall: {
                        fontSize: '13px',
                        padding: '4px 12px',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: '6px',
                        fontSize: '12px',
                    },
                },
            },
        },
    });
}

export const lightTheme = makeTheme('light');
export const darkTheme = makeTheme('dark');

export default darkTheme;
